

import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft, FileText } from "lucide-react";
import { Button } from "../../components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import * as meetingService from "../../services/meeting.service";
import * as projectService from "../../services/project.service";
import * as itemAtaService from "../../services/itemAta.service";
import * as desenvolvimentoService from "../../services/desenvolvimento.service";
import * as topicoService from "../../services/topico.service";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { ExportLanguageDialog, type ExportLanguage } from "../MapaAtas/components/ExportLanguageDialog";
import { usePageTitle } from "../../hooks/usePageTitle";

interface TopicHistory {
  ataNumber: number;
  ataDate: string;
  items: Array<{
    id: string;
    descricao: string;
    concluido: boolean;
    entidade?: string;
    prazo?: string;
    desenvolvimentos: Array<{
      id: string;
      texto: string;
      ata_numero: number;
    }>;
  }>;
}

export default function TopicHistoryPreview() {
  const { i18n } = useTranslation('common');
  const { id: projectId, topicId } = useParams<{
    id: string;
    topicId: string;
  }>();
  const navigate = useNavigate();

  const [projectName, setProjectName] = useState("");
  const [topicTitle, setTopicTitle] = useState("");
  usePageTitle(topicTitle ? `Histórico — ${topicTitle}` : 'Histórico do Tópico');
  const [history, setHistory] = useState<TopicHistory[]>([]);
  const [loading, setLoading] = useState(true);
  const [showExportDialog, setShowExportDialog] = useState(false);

  useEffect(() => {
    loadTopicHistory();
  }, [projectId, topicId]);

  const loadTopicHistory = async () => {
    if (!projectId || !topicId) return;

    try {
      setLoading(true);


      const project = await projectService.getProjectById(projectId);
      setProjectName(project.name);


      const meetingsResponse = await meetingService.getProjectMeetings(projectId);
      const meetings = meetingsResponse.meetings;


      const historyData: TopicHistory[] = [];

      for (const meeting of meetings) {

        const items = await itemAtaService.getItemsByAtaId(meeting.id);


        const topicItems = items.filter(item => item.topico_id === topicId);

        if (topicItems.length > 0) {

          if (!topicTitle) {
            const topicos = await topicoService.getTopicosByAtaId(meeting.id);
            const topico = topicos.find(t => t.id === topicId);
            if (topico) {
              setTopicTitle(topico.titulo);
            }
          }


          const itemsWithDev = await Promise.all(
            topicItems.map(async (item) => {
              const desenvolvimentos = await desenvolvimentoService.getDesenvolvimentosByItemId(item.id);

              return {
                id: item.id,
                descricao: item.descricao,
                concluido: item.concluido,
                entidade: item.entidade,
                prazo: item.prazo,
                desenvolvimentos: desenvolvimentos.map((dev) => ({
                  id: dev.id,
                  texto: dev.texto,
                  ata_numero: dev.ata_numero,
                })),
              };
            })
          );

          historyData.push({
            ataNumber: meeting.number,
            ataDate: meeting.date,
            items: itemsWithDev,
          });
        }
      }


      historyData.sort((a, b) => b.ataNumber - a.ataNumber);
      setHistory(historyData);
    } catch (error) {
      console.error("Erro ao carregar histórico do tópico:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleExportPDF = (language: ExportLanguage) => {
    const originalTitle = document.title;
    const originalLanguage = i18n.language;

    // Define título em branco para não aparecer no cabeçalho do navegador
    document.title = " ";
    
    // Muda temporariamente o idioma para exportação
    i18n.changeLanguage(language);

    setTimeout(() => {
      const html = document.documentElement;
      const wasDark = html.classList.contains('dark');
      if (wasDark) html.classList.remove('dark');

      window.print();

      // Restaura o título e idioma original após impressão
      setTimeout(() => {
        if (wasDark) html.classList.add('dark');
        document.title = originalTitle;
        i18n.changeLanguage(originalLanguage);
      }, 1000);
    }, 100);
  };

  const formatTableDate = (rawDate?: string) => {
    if (!rawDate) return "-";

    try {
      const dateStr = rawDate.includes("T") ? rawDate : `${rawDate}T00:00:00`;
      return format(new Date(dateStr), "dd/MM/yy", { locale: ptBR });
    } catch {
      return rawDate;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p>Carregando histórico do tópico...</p>
      </div>
    );
  }

  return (
    <>
      {}
      <style>{`
        @media print {
          @page {
            size: A4 portrait;
            margin: 15mm 10mm;
          }

          * {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            box-sizing: border-box;
          }

          html, body {
            width: 210mm;
            margin: 0;
            padding: 0;
            overflow: visible;
          }

          body {
            background: white !important;
            font-size: 9pt;
            line-height: 1.3;
          }

          .no-print {
            display: none !important;
          }

          
          h1 {
            font-size: 14pt;
            margin: 0 0 8pt 0;
          }

          h2 {
            font-size: 12pt;
            margin: 8pt 0 6pt 0;
          }

          h3 {
            font-size: 10pt;
            margin: 6pt 0 4pt 0;
          }

          p, td, th, span, div {
            font-size: 8pt;
            line-height: 1.3;
          }

          
          table {
            width: 100%;
            border-collapse: collapse;
            page-break-inside: auto;
            font-size: 8pt;
            table-layout: fixed;
          }

          thead {
            display: table-header-group;
            background-color: #f3f4f6 !important;
          }

          th {
            background-color: #f3f4f6 !important;
            padding: 4pt 3pt;
            font-size: 7pt;
            border: 0.5pt solid #d1d5db;
          }

          tr {
            page-break-inside: avoid;
            page-break-after: auto;
          }

          td {
            border: 0.5pt solid #d1d5db;
            padding: 4pt 3pt;
            font-size: 8pt;
            word-wrap: break-word;
            overflow-wrap: break-word;
            word-break: break-word;
            white-space: normal !important;
          }

          
          .bg-gray-50,
          .bg-gray-100 {
            background-color: #f9fafb !important;
          }

          .bg-green-100 {
            background-color: #dcfce7 !important;
          }

          
          .mb-6, .mb-8 {
            margin-bottom: 8pt !important;
          }

          .mb-4 {
            margin-bottom: 6pt !important;
          }

          .p-4, .p-6, .p-8 {
            padding: 6pt !important;
          }

          
          .page-break {
            page-break-before: always;
          }

          
          .shadow-sm {
            box-shadow: none !important;
          }
        }
        }
      `}</style>

      {}
      <div className="no-print bg-white border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3">
          <div className="flex items-center gap-3">
            {/* Botão voltar */}
            <button
              onClick={() => navigate(`/projects/${projectId}/meetings`)}
              className="p-2 hover:bg-gray-100 rounded-md transition-colors flex-shrink-0"
              aria-label="Voltar para lista de atas"
            >
              <ArrowLeft size={18} className="text-gray-600" />
            </button>

            {/* Info principal */}
            <div className="flex-1 min-w-0 flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3">
              <div className="flex items-center gap-2 min-w-0">
                <h1 className="text-lg font-semibold text-gray-900 truncate">
                  Histórico do Tópico
                </h1>
                <span className="text-gray-400 hidden sm:inline">•</span>
                <span className="text-sm text-gray-600 truncate hidden sm:inline">{topicTitle}</span>
              </div>
            </div>

            {/* Ações */}
            <div className="flex items-center gap-2 flex-shrink-0">
              <Button onClick={() => setShowExportDialog(true)} variant="default" size="sm" className="gap-2">
                <FileText className="h-4 w-4" />
                <span className="hidden sm:inline">Exportar PDF</span>
              </Button>
            </div>
          </div>
        </div>
      </div>

      <ExportLanguageDialog
        open={showExportDialog}
        onOpenChange={setShowExportDialog}
        onExport={handleExportPDF}
      />

      {}
      <div className="min-h-screen bg-gray-100 py-8 px-4 print:bg-white print:p-0">
        <div className="max-w-5xl mx-auto bg-white shadow-sm p-8 print:shadow-none print:p-0">
          {}
          <div className="mb-8 print:mb-6">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              Histórico do Tópico
            </h1>
            <h2 className="text-2xl font-semibold text-[#013EFB] mb-1">
              {topicTitle}
            </h2>
            <p className="text-gray-600">Projeto: {projectName}</p>
            <p className="text-sm text-gray-500 mt-2">
              Gerado em: {format(new Date(), "dd 'de' MMMM 'de' yyyy 'às' HH:mm", { locale: ptBR })}
            </p>
          </div>

          {}
          {history.length === 0 ? (
            <p className="text-gray-500 italic">
              Nenhum item encontrado para este tópico.
            </p>
          ) : (
            history.map((ataHistory, index) => (
              <div
                key={ataHistory.ataNumber}
                className={`mb-8 ${index > 0 ? 'page-break' : ''}`}
              >
                {}
                <div className="border-b-2 border-gray-700 pb-2 mb-4">
                  <h3 className="text-xl font-bold text-gray-900">
                    Ata #{ataHistory.ataNumber}
                  </h3>
                  <p className="text-sm text-gray-600">
                    Data: {format(new Date(ataHistory.ataDate), "dd 'de' MMMM 'de' yyyy", { locale: ptBR })}
                  </p>
                </div>

                {}
                <Table>
                  <TableHeader className="bg-gray-100">
                    <TableRow className="bg-gradient-to-r from-gray-100 to-gray-200 border-b-2 border-gray-700">
                      <TableHead
                        className="text-center font-bold text-gray-900 text-xs uppercase tracking-wide"
                        style={{ width: "5%" }}
                      >
                        ITEM
                      </TableHead>
                      <TableHead
                        className="text-center font-bold text-gray-900 text-xs uppercase tracking-wide border-l border-gray-300"
                        style={{ width: "6%" }}
                      >
                        Nº ATA
                      </TableHead>
                      <TableHead
                        className="text-center font-bold text-gray-900 text-xs uppercase tracking-wide border-l border-gray-300"
                        style={{ width: "8%" }}
                      >
                        DATA
                      </TableHead>
                      <TableHead
                        className="text-left font-bold text-gray-900 text-xs uppercase tracking-wide border-l border-gray-300"
                        style={{ width: "63%" }}
                      >
                        DESCRIÇÃO DOS ASSUNTOS
                      </TableHead>
                      <TableHead
                        className="text-center font-bold text-gray-900 text-xs uppercase tracking-wide border-l border-gray-300"
                        style={{ width: "7%" }}
                      >
                        RESPONSÁVEL
                      </TableHead>
                      <TableHead
                        className="text-center font-bold text-gray-900 text-xs uppercase tracking-wide border-l border-gray-300"
                        style={{ width: "11%" }}
                      >
                        PRAZO
                      </TableHead>
                    </TableRow>
                  </TableHeader>

                  <TableBody>
                    {ataHistory.items.map((item, itemIdx) => {
                      const isResolved = item.concluido;
                      const itemDevs = item.desenvolvimentos.sort(
                        (a, b) => a.ata_numero - b.ata_numero
                      );

                      return (
                        <TableRow
                          key={item.id}
                          className={`${
                            itemIdx % 2 === 0 ? "bg-gray-50" : "bg-gray-100"
                          } hover:bg-gray-200 transition-colors ${
                            isResolved ? "opacity-60" : ""
                          }`}
                        >
                          <TableCell className="text-center align-top font-semibold text-gray-900 border-l border-gray-200">
                            {itemIdx + 1}
                          </TableCell>
                          <TableCell className="text-center align-top font-semibold text-gray-700 border-l border-gray-300">
                            #{ataHistory.ataNumber}
                          </TableCell>
                          <TableCell className="text-center align-top text-sm text-gray-700 border-l border-gray-300">
                            {formatTableDate(ataHistory.ataDate)}
                          </TableCell>
                          <TableCell className="align-top border-l border-gray-300">
                            <div className="space-y-2">
                              <p
                                className={`text-sm text-gray-900 ${
                                  isResolved ? "line-through" : ""
                                }`}
                              >
                                {item.descricao}
                                {isResolved && (
                                  <span className="ml-2 px-2 py-0.5 bg-green-100 text-green-700 text-xs font-bold rounded uppercase">
                                    Concluído
                                  </span>
                                )}
                              </p>

                              {itemDevs.length > 0 && (
                                <div className="space-y-1.5 pt-2 border-t border-gray-200">
                                  {itemDevs.map((dev) => (
                                    <div
                                      key={dev.id}
                                      className="text-sm text-gray-700 whitespace-pre-line"
                                    >
                                      <span className="font-semibold">A{dev.ata_numero}</span> | {dev.texto}
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="text-center align-top text-sm text-gray-700 border-l border-gray-300">
                            {item.entidade || "-"}
                          </TableCell>
                          <TableCell className="text-center align-top text-sm text-gray-700 border-l border-gray-300">
                            {formatTableDate(item.prazo)}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            ))
          )}

          {}
          <div className="mt-12 pt-6 border-t border-gray-300 text-center text-sm text-gray-500">
            <p>Fim do histórico do tópico: {topicTitle}</p>
            <p className="mt-1">Total de atas: {history.length}</p>
          </div>
        </div>
      </div>
    </>
  );
}
